// Silnik tłumaczenia PL → EN (bez DOM-u — testowalny). Słownik: klucz = polski tekst
// ze ściśniętymi białymi znakami, wartość = angielski. Klucz może mieć znaczniki {0}, {1}…
// (z literałów szablonowych i JSX-a „Mecz {n} z {m}") — wtedy to wzorzec.
//
// Kolejność prób dla tekstu: dokładne trafienie → liczby zamienione na {0}… (szybka ścieżka
// wzorców z liczbami) → wzorce z dowolnym wypełnieniem (wypełnienie tłumaczone osobno)
// → wariant wielkości liter (CAPS, pierwsza litera). Nic nie pasuje = null (zostaje tekst
// polski, tylko liczby dostają angielski zapis).

export type Dict = Record<string, string>

const WS = /\s+/g
const norm = (s: string) => s.replace(WS, ' ').trim()

// Liczba w zapisie polskim: opcjonalny znak, grupy tysięcy po twardej spacji, przecinek dziesiętny.
// Separator tysięcy (twarda spacja) zamieniamy przed ściśnięciem białych znaków na GRP,
// żeby norm() go nie zjadł; enNumbers robi z niego przecinek.
const GRP = '\uE000'
const THOUSANDS = /(\d)[\u00a0\u202f](?=\d{3}(?!\d))/g
const NUM = /[-−+]?\d+(?:[\u00a0\u202f\uE000]\d{3})*(?:[.,]\d+)?/g
const HAS_DIGIT = /\d/
const PLACEHOLDER = /\{(\d+)\}/g
const HAS_PLACEHOLDER = /\{\d+\}/
const LETTER = /[A-Za-ząćęłńóśźżĄĆĘŁŃÓŚŹŻ]/
const PL_CHARS = /[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g
const plLeft = (s: string) => (s.match(PL_CHARS)?.length ?? 0)
// Separatory, po których tnie się tekst sklejony z kawałków (od najsilniejszego).
const SEPS = [' · ', ' — ', ' | ', '; ', '. ', ': ', ', ', ' / ']

/** Zapis liczb po angielsku: 1 234,5 → 1,234.5 (tylko polskie separatory, reszta bez zmian). */
export function enNumbers(s: string): string {
  if (!HAS_DIGIT.test(s)) return s
  return s
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(THOUSANDS, '$1,')
    .replaceAll(GRP, ',')
}

const ordinal = (n: number) => {
  const d = n % 10, dd = n % 100
  return n + (dd >= 11 && dd <= 13 ? 'th' : d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th')
}

/** Polska kropka porządkowa i „th" doklejone przez słownik: „75. percentile", „1th" → „1st". */
export function enOrdinals(s: string): string {
  if (!HAS_DIGIT.test(s)) return s
  return s
    .replace(/\b(\d+)(?:st|nd|rd|th)\b/g, (_, n) => ordinal(Number(n)))
    .replace(/\b(\d+)\.(?=\s*(?:percentile|pct|place|position|round|half)\b)/g, (_, n) => ordinal(Number(n)))
}

interface Pattern {
  re: RegExp
  value: string
  anchor: string // najdłuższy kawałek stały — szybki odsiew przez includes()
  order: number[] // numery znaczników w kolejności wystąpienia w kluczu
  spec: number // długość części stałych (bardziej konkretny wygrywa)
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export interface Translator {
  translate: (text: string) => string | null
  size: number
}

const STRUCT_SEP = /[·—–|;:,=/()→]|\. /

/** Wzorzec struktury ma sens tylko, gdy KAŻDE dwa sąsiednie znaczniki dzieli prawdziwy
 * separator — „{0} {1}." cięłoby zdanie na przypadkowe słowa. */
export function usableStructure(p: string): boolean {
  const parts = p.split(/\{\d+\}/)
  return parts.slice(1, -1).every((mid) => STRUCT_SEP.test(mid))
}

export function makeTranslator(dict: Dict, structure: string[] = []): Translator {
  const exact = new Map<string, string>()
  const upper = new Map<string, string>()
  const patterns: Pattern[] = []

  for (const [rawKey, value] of Object.entries(dict)) {
    if (typeof value !== 'string') continue
    const key = norm(rawKey)
    if (!key || value === key) continue
    exact.set(key, value)
    const up = key.toLocaleUpperCase('pl-PL')
    if (up !== key && !upper.has(up)) upper.set(up, value.toUpperCase())
    if (HAS_PLACEHOLDER.test(key)) {
      const statics = key.split(/\{\d+\}/)
      if (!statics.some((s) => LETTER.test(s))) continue // same znaki interpunkcji — nic nie znaczy
      const order: number[] = []
      key.replace(PLACEHOLDER, (_, i) => { order.push(Number(i)); return '' })
      const body = statics.map(escapeRe).join('(.+?)').replace(/ /g, '\\s+')
      const anchor = statics.reduce((a, b) => (b.trim().length > a.length ? b.trim() : a), '')
      patterns.push({ re: new RegExp(`^${body}$`, 's'), value, anchor, order, spec: statics.join('').length })
    }
  }
  patterns.sort((a, b) => b.spec - a.spec)

  // Struktury z kodu (src/i18n/structure.json): tekst = te same kawałki, każdy tłumaczony osobno.
  const structs: Pattern[] = []
  for (const raw of structure) {
    const key = norm(raw)
    if (!usableStructure(key)) continue
    const statics = key.split(/\{\d+\}/)
    const order: number[] = []
    key.replace(PLACEHOLDER, (_, i) => { order.push(Number(i)); return '' })
    const body = statics.map(escapeRe).join('(.+?)').replace(/ /g, '\\s+')
    const anchor = statics.reduce((a, b) => (b.trim().length > a.length ? b.trim() : a), '')
    structs.push({ re: new RegExp(`^${body}$`, 's'), value: key, anchor, order, spec: statics.join('').length })
  }
  structs.sort((a, b) => b.spec - a.spec)

  const fill = (value: string, order: number[], parts: string[]) =>
    value.replace(PLACEHOLDER, (m, i) => {
      const at = order.indexOf(Number(i))
      return at >= 0 && at < parts.length ? parts[at] : m
    })

  const cache = new Map<string, string | null>()

  // Tłumaczenie kawałka wstawionego we wzorzec (bez kolejnych wzorców — głębokość 1).
  const translatePart = (part: string): string => {
    const t = part.trim()
    const hit = LETTER.test(t) ? translateLeaf(t, 1) : null
    if (hit == null) return enNumbers(part)
    return part.replace(t, hit)
  }

  function viaNumbers(key: string): string | null {
    if (!HAS_DIGIT.test(key)) return null
    const nums: string[] = []
    const k = key.replace(NUM, (m) => `{${nums.push(m) - 1}}`)
    const v = exact.get(k)
    if (v == null) return null
    return v.replace(PLACEHOLDER, (m, i) => (nums[Number(i)] != null ? enNumbers(nums[Number(i)]) : m))
  }

  function viaPatterns(key: string): string | null {
    for (const p of patterns) {
      if (p.anchor && !key.includes(p.anchor)) continue
      const m = p.re.exec(key)
      if (!m) continue
      return fill(p.value, p.order, m.slice(1).map(translatePart))
    }
    return null
  }

  function viaCase(key: string): string | null {
    const up = upper.get(key)
    if (up != null) return up
    // „mecze" w zdaniu z kodu, który zrobił lowercase / capitalize na etykiecie
    const first = key.charAt(0)
    const swapped = (first === first.toLowerCase() ? first.toUpperCase() : first.toLowerCase()) + key.slice(1)
    const v = exact.get(swapped)
    if (v == null) return null
    const vf = v.charAt(0)
    return (first === first.toLowerCase() ? vf.toLowerCase() : vf.toUpperCase()) + v.slice(1)
  }

  const simple = (key: string): string | null => exact.get(key) ?? viaNumbers(key) ?? viaCase(key)

  // Tekst sklejony z kawałków („faza: Obrona · efekt: Strata", „Średnia · 18 zespołów")
  // — każdy kawałek osobno; zostaje, jeśli coś się przetłumaczyło.
  function viaSegments(key: string, depth: number): string | null {
    for (const sep of SEPS) {
      if (!key.includes(sep)) continue
      let changed = false
      const out = key.split(sep).map((part) => {
        const t = part.trim()
        const hit = t && LETTER.test(t) ? translateLeaf(t, depth + 1) : null
        if (hit == null) return enNumbers(part)
        changed = true
        return part.replace(t, hit)
      })
      if (changed) return out.join(sep)
    }
    return null
  }

  function viaStructure(key: string): string | null {
    let best: string | null = null
    for (const p of structs) {
      if (p.anchor && !key.includes(p.anchor)) continue
      const m = p.re.exec(key)
      if (!m) continue
      let changed = false
      const parts = m.slice(1).map((part) => {
        const t = part.trim()
        const hit = t && LETTER.test(t) ? translateLeaf(t, 1) : null
        if (hit == null) return enNumbers(part)
        changed = true
        return part.replace(t, hit)
      })
      if (!changed) continue
      const out = fill(p.value, p.order, parts)
      if (best == null || plLeft(out) < plLeft(best)) best = out
      if (plLeft(best) === 0) break
    }
    return best
  }

  function translateLeaf(key: string, depth: number): string | null {
    return simple(key) ?? (depth < 3 ? viaSegments(key, depth) : null)
  }

  function translateKey(key: string): string | null {
    if (!LETTER.test(key)) return null // same liczby / znaki — niżej tylko zapis liczb
    const hit = simple(key)
    if (hit != null) return hit
    // Wzorzec potrafi złapać tylko początek i zostawić resztę po polsku — wygrywa wersja
    // z najmniejszą liczbą polskich liter (remis: kolejność niżej).
    let best: string | null = null
    for (const c of [viaPatterns(key), viaStructure(key), viaSegments(key, 0)]) {
      if (c != null && (best == null || plLeft(c) < plLeft(best))) best = c
    }
    return best
  }

  function translate(text: string): string | null {
    const key = norm(HAS_DIGIT.test(text) ? text.replace(THOUSANDS, '$1' + GRP) : text)
    if (!key) return null
    let out = cache.get(key)
    if (out === undefined) {
      out = translateKey(key)
      if (out == null && HAS_DIGIT.test(key)) {
        const n = enNumbers(key)
        out = n !== key ? n : null
      } else if (out != null) out = enOrdinals(out)
      if (cache.size > 50_000) cache.clear()
      cache.set(key, out)
    }
    if (out == null) return null
    // Białe znaki z brzegów zostają (JSX skleja „Mecz " + {n}).
    const lead = text.match(/^\s*/)![0]
    const trail = text.match(/\s*$/)![0]
    return lead + out + trail
  }

  return { translate, size: exact.size }
}
